/**
 * Astor Agent DTO Schemas (UX v3 #1739)
 *
 * Represents the BIC ("best-in-class") Astor agent personas surfaced in the
 * Control Center / Astor Autonomy page. Backed by the `bic_agents` table in
 * 8-agent-service (one row per persona per business). The runtime
 * `AgentConfig` row is a separate concept (admin-managed LLM config); these
 * DTOs intentionally only carry catalog fields the autonomy UI needs.
 */

import { z } from 'zod';
import { AstorAutonomyLevel } from '../enums';

/**
 * Astor Agent (BicAgent row, projected for the UI).
 *
 * `autonomyLevel` is nullable: NULL means the agent inherits from the
 * business-level master autonomy on `Business.autonomyLevel`. When set,
 * it overrides the master for this specific agent.
 */
export const AstorAgentSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  code: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  portal: z.enum(['consumer', 'business', 'admin']),
  defaultModel: z.string(),
  toolCallingEnabled: z.boolean(),
  autonomyLevel: z.nativeEnum(AstorAutonomyLevel).nullable(),
  bicVersionAtSeed: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type AstorAgent = z.infer<typeof AstorAgentSchema>;

/**
 * Input for `astorAgents.updateAutonomyLevel`. Setting `autonomyLevel` to
 * `null` clears the per-agent override (agent reverts to master).
 */
export const UpdateAstorAgentAutonomyLevelSchema = z.object({
  agentId: z.string().uuid(),
  autonomyLevel: z.nativeEnum(AstorAutonomyLevel).nullable(),
});

export type UpdateAstorAgentAutonomyLevel = z.infer<typeof UpdateAstorAgentAutonomyLevelSchema>;
